import type { AsyncState } from '../../../platform/async/Async'
import type { LocalDate } from '../../../platform/localDate'
import { MONTH_LONG, MONTH_SHORT, unitStart } from '../../cashflow/cashflowPeriod'
import type { Cashflow } from '../../cashflow/cashflowWire'

export const STRIP_BARS = 7

export type StripBar = { start: LocalDate; label: string; expenseCents: number; current: boolean }

export type SpentView = {
  /** "October". */
  month: string
  currency: string
  /** The server's `totals.expense` for the month. */
  expenseCents: number
  /** The server's `prior.expense`, named by its own month; null when there is no comparison window. */
  prior: { label: string; cents: number } | null
  /** The last seven months of the server's `buckets[].expense`, or null when there is no history to draw. */
  strip: StripBar[] | null
}

const monthIndex = (d: LocalDate): number => Number(d.slice(5, 7)) - 1

/**
 * The period call (and, for an entitled user, the history call) as the widget's
 * state. Every figure is a field the server returned; the strip is a slice of
 * the history, not a computation. A failed or absent history only drops the
 * strip — the figure above it does not depend on it.
 *
 * Empty is "accounts exist but nothing was spent" (R8), distinct from the
 * page-level "no accounts".
 */
export function toSpentState(
  period: AsyncState<Cashflow>,
  history: AsyncState<Cashflow> | null,
  today: LocalDate,
): AsyncState<SpentView> {
  if (period.status !== 'success' && period.status !== 'stale') return period
  const c = period.data
  if (c.totals.expenseCents === 0 && c.scope.accounts > 0) return { status: 'empty' }
  const current = unitStart(today, 'month')
  const periods = history !== null && (history.status === 'success' || history.status === 'stale') ? history.data.periods : null
  return {
    status: 'success',
    data: {
      month: MONTH_LONG[monthIndex(current)] ?? '',
      currency: c.currency,
      expenseCents: c.totals.expenseCents,
      prior: c.prior === null ? null : { label: MONTH_LONG[monthIndex(c.prior.start)] ?? '', cents: c.prior.expenseCents },
      strip:
        periods === null
          ? null
          : periods.slice(-STRIP_BARS).map((p) => ({
              start: p.start,
              label: MONTH_SHORT[monthIndex(p.start)] ?? '',
              expenseCents: p.expenseCents,
              current: p.start === current,
            })),
    },
  }
}
